import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Lock } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_e5fbfad1-91cd-48c5-b4b1-e04fdc4a0794/artifacts/hl0vo1yr_PROOF%20logo%20and%20concept%20round.png";

export default function Login() {
    const { authed, login } = useAuth();
    const navigate = useNavigate();
    const loc = useLocation();
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    // Block search engines from indexing the login page.
    useEffect(() => {
        const m = document.createElement("meta");
        m.name = "robots";
        m.content = "noindex,nofollow";
        document.head.appendChild(m);
        const prev = document.title;
        document.title = "Sign in · ProofCommand";
        return () => {
            document.head.removeChild(m);
            document.title = prev;
        };
    }, []);

    // If already authed, bounce away.
    useEffect(() => {
        if (authed === true) {
            const target = loc.state?.from || "/dashboard";
            navigate(target, { replace: true });
        }
    }, [authed, navigate, loc.state]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!pw) return;
        setBusy(true);
        try {
            await login(pw);
        } catch (e2) {
            const detail =
                e2?.response?.data?.detail ||
                e2?.response?.statusText ||
                e2?.message ||
                "login failed";
            setErr(typeof detail === "string" ? detail : "login failed");
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen pc-grid-bg bg-[#090a0b] text-[#f3f4f6] flex items-center justify-center p-6"
            data-testid="login-page"
        >
            <form
                onSubmit={submit}
                className="w-full max-w-sm border border-[#262a2e] bg-[#111315] p-8 pc-fade-up"
            >
                <div className="flex items-center gap-3 mb-6">
                    <img src={LOGO_URL} alt="ProofCommand" className="w-10 h-10 object-contain" />
                    <div>
                        <div className="font-sans font-bold tracking-tight">ProofCommand</div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#9ca3af]">
                            authorized operators only
                        </div>
                    </div>
                </div>

                <label className="block mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af] mb-1.5">
                        password
                    </div>
                    <div className="relative">
                        <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]" />
                        <input
                            type="password"
                            autoFocus
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            data-testid="login-password"
                            className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] pl-9 pr-3 py-2.5 focus:border-[#7cc93c] outline-none"
                        />
                    </div>
                </label>

                {err && (
                    <div
                        data-testid="login-error"
                        className="mb-3 font-mono text-[11px] text-[#ff453a] border border-[#ff453a]/30 bg-[#ff453a]/5 px-3 py-2"
                    >
                        {err}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="login-submit"
                    className="w-full font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-2.5 rounded-[2px] disabled:opacity-50"
                >
                    {busy ? "signing in…" : "sign in"}
                </button>

                <div className="mt-6 font-mono text-[10px] text-[#9ca3af] text-center leading-relaxed">
                    no claims · receipts · proofcommand
                </div>
            </form>
        </div>
    );
}
